// ExchangesV2 — batch 5's rebuild of Exchanges (UI rebuild, 27 Oct 2026; drawn when lib/ui-v2.ts switches batch 5 on).
//
// ASTRA'S EXCHANGES ("one job: compare venue holdings with the desk's records"; the operator's ruling of 26 Oct: closer to
// Astra). The CEX work is PARKED, so this is LAYOUT ONLY — no new read, no new behaviour. The page is: the head (Movements
// and Fills behind a tap, Refresh); a failed read or the accounts not read, one line each, the detail behind a tap; ONE
// table of venue holdings — "Bitget · Main" over "USDT · Spot", the venue balance, the reading (its age, and "Read failed"
// or "Stale" on the rows that are) — the exceptions first, the dust rows folded; then the advisor's items, one line each.
//
// Gone, as Astra says (duplicates): the per-desk roll-up table above the accounts (the same balances again, summed per
// asset) and the per-account panels (one table now). Behind a tap: movements, fills, the advisor's action / detail / rule
// book, the "what to do" for a rejected key. NOT drawn: Astra's Books column and search — /api/cex carries no books
// figure and cannot search, and the CEX work is parked (no new read); filtering only the rows read would claim a search
// the books never ran.
//
// Kept from the old screen, each from the same code: the queries and keys (['cex'] every minute, ['cex-movements'] and
// ['cex-fills', entity, 30] only while open, ['cex-advice'] every minute) through lib/api.ts's one client (its bound and
// its refusal in words); the desk filter (only with more than one desk) and the desk name only when more than one is
// shown; stale and unreachable said, never hidden — a failed account's figures muted, its reading named; "Nothing held."
// and "Nothing to fix" only from a read that succeeded; the advisor's fix / advise / note split and its "Nothing to do"
// filter; "Record the move →" to Move money from a movement the chain saw (moveMoneyUrl, verbatim); nothing here moves
// money.
import { useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { api, request } from '../../lib/api';
import { readWords } from '../../lib/errors';
import { readState, offlineFailure } from '../../lib/read-state';
import { displayAsset, fmtAmount, fmtRelative, isDustFigure } from '../../lib/format';
import { brandCase, dustFor } from '../../lib/naming';
import { Pill } from '../../components/kit';
import { EmptyState, Notice, PageHead, Panel, Register, SkeletonRows, SummaryStrip, TAP, type RegisterColumn, OfflineNotice } from '../../components/v2/kit';
import { AdvicePanel } from '../../components/v2/exchanges/Advice';
import { FillsSheet, MovementsSheet } from '../../components/v2/exchanges/Sheets';
import {
  ENTITY_LABEL, ERROR_KIND, FILLS_DAYS, type AccountRow, type CexResponse, type MovementRow, type PositionRow,
} from '../../components/v2/exchanges/rules';

type View = 'movements' | 'fills';
const isView = (s: string | null): s is View => s === 'movements' || s === 'fills';

export function ExchangesV2() {
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const viewParam = search.get('view');
  const view: View | null = isView(viewParam) ? viewParam : null;
  const openedHere = useRef(false);
  const openView = (v: View) => {
    openedHere.current = true;
    const next = new URLSearchParams(search);
    next.set('view', v);
    setSearch(next);
  };
  const closeView = () => {
    if (openedHere.current) { openedHere.current = false; navigate(-1); return; }
    const next = new URLSearchParams(search);
    next.delete('view');
    setSearch(next, { replace: true });
  };

  const [entity, setEntity] = useState<string | 'all'>('all');
  const show = (e: string) => entity === 'all' || entity === e;

  // ── the reads: the old screen's, key for key ──
  const q = useQuery({
    queryKey: ['cex'],
    queryFn: () => request<CexResponse>('/api/cex'),
    refetchInterval: 60_000,       // the poller writes every 10 min; a 1-min UI refresh keeps the ages honest
  });
  const mv = useQuery({
    queryKey: ['cex-movements'],
    queryFn: () => request<{ rows: MovementRow[] }>('/api/cex/movements?limit=200'),
    enabled: view === 'movements',
  });
  // The summary is per venue and carries no desk of its own, so the desk filter is asked of the BFF.
  const fl = useQuery({
    queryKey: ['cex-fills', entity, FILLS_DAYS],
    queryFn: () => api.cexFills({
      entity: entity === 'all' ? undefined : entity,
      from: new Date(Date.now() - FILLS_DAYS * 24 * 3600 * 1000).toISOString(),
      limit: 500,
    }),
    enabled: view === 'fills',
  });
  const adv = useQuery({
    queryKey: ['cex-advice'],
    queryFn: () => api.cexAdvice(),
    refetchInterval: 60_000,
  });
  const read = readState(q);

  const entities = q.data?.entities ?? [];
  const positions = q.data?.positions ?? [];
  // THE ROLL-UP PER ASSET, ACROSS THE VENUES (restored 1 Nov 2026 — the old screen's per-desk table): total, what is in use
  // and on how many accounts, as one figure strip over the table; dust left out.
  const rollup = useMemo(() => {
    const by = new Map<string, { asset: string; total: number; used: number; accounts: number; unread: number }>();
    for (const r of q.data?.rollup ?? []) {
      if (!show(r.entity)) continue;
      const x = by.get(r.asset) ?? { asset: r.asset, total: 0, used: 0, accounts: 0, unread: 0 };
      x.total += Number(r.total); x.used += Number(r.used); x.accounts += r.accounts; x.unread += Number(r.unreachable_accounts) || 0;
      by.set(r.asset, x);
    }
    return [...by.values()].filter((x) => Math.abs(x.total) > dustFor(x.asset)).sort((a, b) => b.accounts - a.accounts || a.asset.localeCompare(b.asset));
  }, [q.data, entity]);
  const accounts = q.data?.accounts ?? [];
  const adviceItems = useMemo(
    () => (adv.data?.items ?? []).filter((i) => i.entity === null || show(i.entity)),
    [adv.data, entity],
  );
  const fixCount = adviceItems.filter((i) => i.severity === 'fix').length;

  // Accounts that failed (or never ran) their last sync — the line that is work.
  const unreachable = useMemo(() => accounts.filter((a) => show(a.entity) && a.last_run_ok !== true), [accounts, entity]);
  const multiDesk = entities.filter(show).length > 1;
  const deskPrefix = (e: string) => (multiDesk ? `${ENTITY_LABEL[e] ?? e} · ` : '');

  // ONE ROW PER ACCOUNT · WALLET · ASSET, the exceptions first (a failed read, then a stale one), the dust rows folded.
  const [dustOpen, setDustOpen] = useState(false);
  const isDust = (p: PositionRow) => isDustFigure(p.total, p.asset);
  const rank = (p: PositionRow) => (p.last_run_ok === false ? 0 : p.stale ? 1 : 2);
  const allRows = useMemo(
    () => positions.filter((p) => show(p.entity)).map((p, i) => ({ p, i })).sort((a, b) => rank(a.p) - rank(b.p) || a.i - b.i).map((x) => x.p),
    [positions, entity],
  );
  const dust = allRows.filter(isDust).length;
  const rows = dustOpen ? allRows : allRows.filter((p) => !isDust(p));

  // ONE FACT PER COLUMN (his "one fact per place", 1 Nov 2026): the balance, what is in use, when it was read and its state
  // each in their own seat — "Read failed · 4h ago" and "100,000 in use" under a figure were two facts in one cell.
  const columns: RegisterColumn<PositionRow>[] = [
    { key: 'account', head: 'Venue / account', cell: (p) => <Account p={p} prefix={deskPrefix(p.entity)} /> },
    { key: 'balance', head: 'Venue balance', align: 'right', width: 'w-[11rem]', cell: (p) => <Balance p={p} /> },
    { key: 'used', head: 'In use', align: 'right', width: 'w-[10rem]', cell: (p) => <Used p={p} /> },
    { key: 'read', head: 'Read', align: 'right', width: 'w-[7rem]', cell: (p) => <span className="whitespace-nowrap text-tg-hint">{fmtRelative(p.as_of)}</span> },
    { key: 'state', head: 'State', align: 'right', width: 'w-[8rem]', cell: (p) => <Reading p={p} /> },
  ];

  return (
    <>
      <PageHead
        title="Exchanges"
        // The count of things to fix is the advice panel's own title, right under the head now (said once).
        actions={
          <>
            <button type="button" className="btn-secondary px-3 text-body" onClick={() => openView('movements')}>Movements</button>
            <button type="button" className="btn-secondary px-3 text-body" onClick={() => openView('fills')}>Fills</button>
            <button type="button" onClick={() => q.refetch()} disabled={q.isFetching} className="btn-secondary px-3 text-body disabled:opacity-50">
              <RefreshCw aria-hidden className={`h-4 w-4 ${q.isFetching ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </>
        }
      >
        {/* The desk filter — only the desks this admin can see, and only when there is a choice. */}
        {entities.length > 1 && (
          <div>
            <label className="sr-only" htmlFor="cex-desk">Desk</label>
            <select id="cex-desk" className="select w-auto min-w-[160px] pr-8 text-field lg:text-body" value={entity} onChange={(e) => setEntity(e.target.value)}>
              {['all', ...entities].map((e) => <option key={e} value={e}>{e === 'all' ? 'All desks' : (ENTITY_LABEL[e] ?? e)}</option>)}
            </select>
          </div>
        )}
      </PageHead>

      {read === 'failed' && offlineFailure(q.error) && <OfflineNotice what="exchanges" kept={!!q.data} />}
      {read === 'failed' && !offlineFailure(q.error) && (
        <Notice tone="bad" action={
          <button type="button" className="btn-link text-body" disabled={q.isFetching} onClick={() => q.refetch()}>{q.isFetching ? 'Trying…' : 'Try again'}</button>
        }>
          <Fold error={q.error}>Couldn&apos;t read exchanges.</Fold>
        </Notice>
      )}

      {/* UNREACHABLE FIRST — a down venue must not hide behind a confident-looking table. One line; who and why behind it. */}
      {unreachable.length > 0 && <NotRead accounts={unreachable} prefix={deskPrefix} />}

      {/* THE DAILY FIX LEADS (the audit, 1 Nov 2026: it sat under every holding, its action folded). */}
      <AdvicePanel items={adviceItems} failed={adv.isError} error={adv.error} onRetry={() => adv.refetch()} />

      {/* The totals only when one of them is a figure: a strip of "not read" cells under the not-read notice adds nothing. */}
      {rollup.length > 0 && read !== 'waiting' && rollup.some((x) => x.unread === 0) && (
        // A total that takes in an account whose last read failed is not known (Appendix B): said so, never a figure.
        <SummaryStrip cols={rollup.length >= 4 ? 4 : 3} items={rollup.map((x) => ({
          label: displayAsset(x.asset),
          value: x.unread > 0
            ? <span className="font-sans text-body font-normal text-tg-hint" title={`${x.unread} of ${x.accounts} accounts not read`}>not read</span>
            : fmtAmount(x.total, x.asset),
          sub: x.unread > 0 ? undefined : (
            <>
              <span className="block">{x.accounts} {x.accounts === 1 ? 'account' : 'accounts'}</span>
              {x.used > 0 && <span className="block">In use <span className="font-mono tabular text-tg-text">{fmtAmount(x.used, x.asset)}</span></span>}
            </>
          ),
        }))} />
      )}
      {read === 'waiting' && <Panel flush className="mb-4 lg:mb-5"><SkeletonRows what="exchanges" /></Panel>}
      {read === 'known' && allRows.length === 0 && <Panel className="mb-4 lg:mb-5"><EmptyState>Nothing held.</EmptyState></Panel>}
      {/* A failed refresh keeps its rows beside the error (the old screen did the same); the rows say their own age. */}
      {allRows.length > 0 && read !== 'waiting' && (
        <Panel flush className="mb-4 lg:mb-5">
          <Register
            label="Venue holdings" rows={rows} rowKey={(p) => `${p.account_id}:${p.wallet}:${p.asset}`} columns={columns}
            card={(p) => ({
              title: <span className="block truncate" title={`${deskPrefix(p.entity)}${brandCase(p.venue)} · ${brandCase(p.label)}`}>{deskPrefix(p.entity)}{brandCase(p.venue)} · {brandCase(p.label)}</span>,
              figure: <span className="block text-right"><Balance p={p} />{Number(p.used) > 0 && <span className="mt-0.5 block text-meta text-tg-hint">In use <Used p={p} /></span>}</span>,
              meta: <><span>{displayAsset(p.asset)}</span> · <span className="capitalize">{p.wallet}</span> · read {fmtRelative(p.as_of)}</>,
              state: <Reading p={p} />,
            })}
          />
          {dust > 0 && (
            <div className="border-t border-stroke-soft px-4 py-3 lg:px-5">
              <button type="button" className={TAP} aria-expanded={dustOpen} onClick={() => setDustOpen((v) => !v)}>
                {dustOpen ? `Hide ${dust} near zero` : `+ ${dust} near zero`}
              </button>
            </div>
          )}
        </Panel>
      )}


      {view === 'movements' && <MovementsSheet mv={mv} onClose={closeView} />}
      {view === 'fills' && <FillsSheet fl={fl} onClose={closeView} />}
    </>
  );
}

/** "Bitget · Main" over "USDT · Spot". */
function Account({ p, prefix }: { p: PositionRow; prefix: string }) {
  return (
    <span className="block min-w-0">
      {/* Two lines, never cut at one point: two OKX accounts cut alike read as one (the audit at 1024, 1 Nov 2026). */}
      <span className="line-clamp-2 block break-words font-medium" title={`${prefix}${brandCase(p.venue)} · ${brandCase(p.label)}`}>{prefix}{brandCase(p.venue)} · {brandCase(p.label)}</span>
      <span className="mt-0.5 block font-normal text-meta text-tg-hint"><span>{displayAsset(p.asset)}</span> · <span className="capitalize">{p.wallet}</span></span>
    </span>
  );
}

/** The venue's figure — muted when the account's last read failed (the last balance known, not a live one). */
function Balance({ p }: { p: PositionRow }) {
  const failed = p.last_run_ok === false;
  return (
    <span className="whitespace-nowrap">
      <span className={`font-mono tabular ${failed ? 'text-tg-hint' : ''}`}>{fmtAmount(p.total, p.asset)}</span>{' '}
      <span className="text-meta text-tg-hint">{displayAsset(p.asset)}</span>
    </span>
  );
}

/** What is in use (in orders, staked), with its unit; "—" when nothing is. */
function Used({ p }: { p: PositionRow }) {
  if (!(Number(p.used) > 0)) return <span className="text-tg-hint">—</span>;
  return (
    <span className="whitespace-nowrap">
      <span className="font-mono tabular">{fmtAmount(p.used, p.asset)}</span> <span className="text-meta text-tg-hint">{displayAsset(p.asset)}</span>
    </span>
  );
}

/** The reading's state, only when it is not a good one (its age is the Read column's). */
function Reading({ p }: { p: PositionRow }) {
  const failed = p.last_run_ok === false;
  return failed ? <Pill tone="bad">Read failed</Pill> : p.stale ? <Pill tone="warn">Stale</Pill> : null;
}

/** "N accounts not read — totals may be incomplete", one amber line; who, why, when last tried and — for a key that
 *  needs a human — what to do, behind "Details". */
function NotRead({ accounts, prefix }: { accounts: AccountRow[]; prefix: (e: string) => string }) {
  const [open, setOpen] = useState(false);
  const n = accounts.length;
  return (
    <>
      <Notice tone="warn" action={
        <button type="button" className="tap-44 btn-link text-body" aria-expanded={open} onClick={() => setOpen((v) => !v)}>{open ? 'Hide' : 'Details'}</button>
      }>
        {n} account{n > 1 ? 's' : ''} not read — totals may be incomplete
      </Notice>
      {open && (
        <Panel flush className="-mt-2 mb-4 lg:mb-5">
          <ul>
            {accounts.map((a) => {
              const kind = a.last_run_error_kind;
              const fix = a.last_run_error_permanent && kind ? ERROR_KIND[kind]?.fix : '';
              return (
                <li key={a.account_id} className="border-b border-stroke-soft px-4 py-3 last:border-b-0 lg:px-5">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="min-w-0 break-words text-body font-medium">{prefix(a.entity)}{brandCase(a.venue)} · {brandCase(a.label)}</span>
                    {kind && <Pill tone={a.last_run_error_permanent ? 'bad' : 'warn'}>{ERROR_KIND[kind]?.label ?? kind}</Pill>}
                    <span className="text-meta text-tg-hint">{a.last_run_at ? `last tried ${fmtRelative(a.last_run_at)}` : 'never synced'}</span>
                  </div>
                  {fix && <p className="mt-1 break-words text-meta text-tg-hint">{fix.charAt(0).toUpperCase() + fix.slice(1)}.</p>}
                </li>
              );
            })}
          </ul>
        </Panel>
      )}
    </>
  );
}

/** A failed read's line, with the server's words a tap away. */
function Fold({ error, children }: { error: unknown; children: string }) {
  const [more, setMore] = useState(false);
  return (
    <span className="inline-flex flex-wrap items-baseline gap-x-3">
      <span>{children}</span>
      <button type="button" className="tap-44 btn-link text-meta" aria-expanded={more} onClick={() => setMore((v) => !v)}>Details</button>
      {more && <span className="basis-full break-words text-meta">{readWords(error)}</span>}
    </span>
  );
}
